"use strict";

var _interopRequireDefault = require("@babel/runtime/helpers/interopRequireDefault");
Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.ApplicationInsights = void 0;
var _defineProperty2 = _interopRequireDefault(require("@babel/runtime/helpers/defineProperty"));
var _applicationinsightsReactJs = require("@microsoft/applicationinsights-react-js");
var _applicationinsightsWeb = require("@microsoft/applicationinsights-web");
var _EventEmitter = require("../internal/EventEmitter");
var _printWarningWithTrace = require("../internal/printWarningWithTrace");
var _pageViewInitializer = require("../internal/telemetry/browser/pageViewInitializer");
var _createFilterTelemetryProcessor = require("../internal/telemetry/createFilterTelemetryProcessor");
var _createFixedRateSampler = require("../internal/telemetry/createFixedRateSampler");
var _enhanceDependencyDataProcessor = require("../internal/telemetry/server/enhanceDependencyDataProcessor");
var _excludeHealthProbesTelemetryProcessor = require("../internal/telemetry/server/excludeHealthProbesTelemetryProcessor");
var _excludeNextjsTelemetryProcessor = require("../internal/telemetry/server/excludeNextjsTelemetryProcessor");
var _excludeSwaggerTelemetryProcessor = require("../internal/telemetry/server/excludeSwaggerTelemetryProcessor");
var _ApplicationInsightsNotInitializedError = require("./ApplicationInsightsNotInitializedError");
var _isServerApplicationInsightsClient = require("./isServerApplicationInsightsClient");
class ApplicationInsights {
  /**
   * Destroys internal Application Insights, DO NOT USE OUTSIDE OF @ori/logger
   *
   * @internal
   */
  static _reset() {
    this.realApplicationInsights = null;
  }

  /**
   * Returns Application Insights if its initialized, othervise throws
   *
   * @throws ApplicationInsightsNotInitializedError Application Insights must be initialized
   */
  static get() {
    const value = this.realApplicationInsights;
    if (!value) {
      throw new _ApplicationInsightsNotInitializedError.ApplicationInsightsNotInitializedError();
    }
    return value;
  }

  /**
   * Gets you typed client-side Application Insights
   */
  static getForClient() {
    const value = this.get();
    if ((0, _isServerApplicationInsightsClient.isServerApplicationInsightsClient)(value)) {
      throw new Error('Cannot get Application Insights for client, because getForClient was probably called on server');
    }
    return value;
  }

  /**
   * Gets you typed server-side Application Insights
   */
  static getForServer() {
    const value = this.get();
    if (!(0, _isServerApplicationInsightsClient.isServerApplicationInsightsClient)(value)) {
      throw new Error('Cannot get Application Insights for server, because getForServer was probably called on client');
    }
    return value;
  }

  /**
   * Attaches listener that lets you listen for event when application insights was created for environment.
   *
   * This allows you to make changes on application insights before first log is send to application insights itself
   *
   * @param listener
   */
  static onInitialized(listener) {
    const eventListener = ({
      instance
    }) => {
      listener(instance);
    };

    // If its initialized already then we need invoke listener rightaway
    if (this.isInitialized()) {
      eventListener({
        instance: this.get()
      });
    }
    this.eventEmitter.addListener('created', eventListener);
    return {
      /**
       * Removes listener
       */
      remove: () => this.eventEmitter.removeListener('created', eventListener)
    };
  }

  /**
   * Checks if Application Insights is initialized
   */
  static isInitialized() {
    return !!this.realApplicationInsights;
  }

  /**
   * Initializes application insights for current environment and attaches it to this.
   * If it is already initialized then in will just return without doing anything.
   *
   * Automatic `pageView` tracking is disabled to prevent `pageView` from being sent multiple times.
   * See the example how to track `pageView`:
   * @example
   * import { ApplicationInsights } from '@ori/logger';
   * ApplicationInsights.trackPageView();
   */
  static initialize(input) {
    if (this.isInitialized()) {
      return;
    }
    const fallbackOptions = global.oriAppInsightsSettings;
    const options = input ?? fallbackOptions ?? {};
    const {
      connectionString,
      samplingPercentage = 100
    } = options;
    if (!connectionString) {
      // TODO (OL): Should we do some checking to hide this warning when certain environment variable is provided?
      // eslint-disable-next-line no-console -- we want to use native console instead
      console.warn('Could not initialize Application Insights due to missing connectionString');
      return;
    }

    // @ts-expect-error -- okay for here
    const isInEdgeRuntime = typeof EdgeRuntime === 'string';
    if (typeof window === 'undefined' && !isInEdgeRuntime) {
      const {
        isMainThread
      } =
      // eslint-disable-next-line node/global-require, @typescript-eslint/no-require-imports -- caller is not async
      require('node:worker_threads');
      const serverApplicationInsights =
      // This is manily for edge where edge will throw that it cannot find application insights
      // and it will keep the sync import of module on server where we should no await import of application insights
      // eslint-disable-next-line node/global-require, @typescript-eslint/no-require-imports -- Explanation on the previous line
      require('applicationinsights');
      serverApplicationInsights.setup(connectionString).setAutoCollectExceptions(isMainThread).setSendLiveMetrics(isMainThread).setAutoCollectConsole(false).setAutoCollectRequests(false) // is handled by our appshell-server
      .setAutoCollectPerformance(isMainThread).setAutoDependencyCorrelation(isMainThread).setAutoCollectIncomingRequestAzureFunctions(isMainThread);
      serverApplicationInsights.start();
      serverApplicationInsights.defaultClient.addTelemetryProcessor(_excludeHealthProbesTelemetryProcessor.excludeHealthProbesTelemetryProcessor);
      serverApplicationInsights.defaultClient.addTelemetryProcessor(_excludeNextjsTelemetryProcessor.excludeNextjsTelemetryProcessor);
      serverApplicationInsights.defaultClient.addTelemetryProcessor(_excludeSwaggerTelemetryProcessor.excludeSwaggerTelemetryProcessor);
      serverApplicationInsights.defaultClient.addTelemetryProcessor(_enhanceDependencyDataProcessor.enhanceDependencyDataProcessor);
      serverApplicationInsights.defaultClient.config.samplingPercentage = samplingPercentage;
      this.realApplicationInsights = serverApplicationInsights;
    } else {
      // browserHistory.location.pathname = browserHistory.state.url
      const config = {
        ...options,
        samplingPercentage,
        extensions: [],
        extensionConfig: {}
      };
      if (!isInEdgeRuntime) {
        const browserHistory = {
          url: '',
          location: {
            pathname: ''
          },
          listen: () => {},
          ...window.history
        };
        const reactPlugin = new _applicationinsightsReactJs.ReactPlugin();
        config.extensions.push(reactPlugin);
        config.extensionConfig[reactPlugin.identifier] = {
          history: browserHistory
        };
      }
      const applicationInsights = new _applicationinsightsWeb.ApplicationInsights({
        config
      });

      // Enable Beacon API and disable XHR to try avoid double telemetry
      applicationInsights.config.isBeaconApiDisabled = false;
      applicationInsights.config.disableXhr = true;
      applicationInsights.loadAppInsights();
      applicationInsights.addTelemetryInitializer(_pageViewInitializer.pageViewInitializer);
      applicationInsights.addTelemetryInitializer((0, _createFilterTelemetryProcessor.createFilterTelemetryProcessor)(envelope => envelope.data?.baseType === 'MetricData' && ['LCP', 'CLS', 'INP'].includes(envelope.name), (0, _createFixedRateSampler.createFixedRateSampler)(10)));
      if (!isInEdgeRuntime) {
        window.appInsights = applicationInsights;
        const flushAppInsights = () => {
          try {
            applicationInsights.flush();
          } catch {
            // Suppress flush errors during unload — can safely fail if not yet initialized
          }
        };
        window.addEventListener('beforeunload', flushAppInsights);
        window.addEventListener('pagehide', flushAppInsights);
      }
      this.realApplicationInsights = applicationInsights;
    }
    const loggerDebugIsEnabled = process.env.DEBUG_LOGGER === 'true';
    if (loggerDebugIsEnabled) {
      (0, _printWarningWithTrace.printWarningWithTrace)('AppInsights initialized');
    }
    this.eventEmitter.emit('created', {
      instance: this.realApplicationInsights
    });
  }
}
exports.ApplicationInsights = ApplicationInsights;
(0, _defineProperty2.default)(ApplicationInsights, "realApplicationInsights", null);
(0, _defineProperty2.default)(ApplicationInsights, "eventEmitter", new _EventEmitter.EventEmitter());
//# sourceMappingURL=ApplicationInsights.js.map